import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UserPlus, Home, Building2, Users, Handshake, ArrowRight, Sparkles, Inbox, TrendingUp, Contact, CalendarClock, CalendarDays, LayoutGrid,
} from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { Fade } from "@/components/proptech";
import { SalesCoachModule } from "@/components/SalesCoachModule";
import { PropConnectSpotlight } from "@/components/PropConnectSpotlight";
import { VerificationBanner } from "@/components/VerificationBanner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function greetingFor(date = new Date()) {
  const h = date.getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
}

/* -------------------- Integrated metric cell (clickable) -------------------- */
function MetricCell({ icon: Icon, label, value, to, onClick, tone = "navy", testid, sublabel, subTo, subTestid }) {
  const n = Number(value);
  const active = Number.isFinite(n) && n > 0;
  const chip = !active
    ? "bg-secondary text-slate-400"
    : tone === "gold" ? "bg-gold/15 text-gold-600" : "bg-navy/10 text-navy";
  const subNode = sublabel ? (
    to ? (
      <span data-testid={subTestid}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-gold-600 mt-0.5">
        {sublabel} <ArrowRight className="h-3 w-3" />
      </span>
    ) : (
      <Link to={subTo} data-testid={subTestid} onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 text-[11px] font-semibold text-gold-600 mt-0.5 hover:gap-1.5 transition-all">
        {sublabel} <ArrowRight className="h-3 w-3" />
      </Link>
    )
  ) : null;
  const inner = (
    <div className="flex items-center gap-3">
      <span className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 transition-colors ${chip}`}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0">
        <div className={`font-display text-2xl font-bold leading-none ${active ? "text-navy" : "text-slate-400"}`}>{value}</div>
        <div className="text-[11px] text-slate-500 mt-1 leading-tight">{label}</div>
        {subNode}
      </div>
    </div>
  );
  const cls = "block w-full text-left px-4 py-3 transition-colors hover:bg-secondary/40 active:bg-secondary/60";
  if (to) return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
  return <button type="button" onClick={onClick} data-testid={testid} className={cls}>{inner}</button>;
}

/* -------------------- Legacy fallback builder --------------------
   If the consolidated endpoint fails, rebuild reduced KPIs from the existing
   endpoints so the page never crashes (graceful degradation). */
function buildLegacy({ stats, collabs }) {
  const acceptedCount = collabs.filter((c) => c.stage === "Accepted").length;
  return {
    opportunities: [],
    kpis: {
      new_matches: stats?.my_new_matches ?? 0,
      active_buyer_needs: stats?.my_active_buyers ?? 0,
      active_properties: stats?.my_active_properties ?? 0,
      new_inquiries: null,
      active_collaborations: acceptedCount,
    },
    summary: null,
    degraded: true,
  };
}

export default function MyCommandCenter() {
  const { user } = useAuth();
  const verifiedPro = !!user && (user.role === "ADMIN" || user.verification_status === "PROFESSIONAL_VERIFIED");
  const navigate = useNavigate(); // eslint-disable-line
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [briefing, setBriefing] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/opportunities")
      .then((r) => { if (alive) { setData(r.data); setLoaded(true); } })
      .catch(() => {
        Promise.allSettled([api.get("/stats"), api.get("/collaborations")]).then(([s, c]) => {
          if (!alive) return;
          const legacy = buildLegacy({
            stats: s.status === "fulfilled" ? s.value.data : null,
            collabs: c.status === "fulfilled" ? c.value.data || [] : [],
          });
          setData(legacy);
          setLoaded(true);
        });
      });
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    if (!loaded || !data || data.degraded) return;
    if (!(data.opportunities || []).length) return;
    let alive = true;
    api.get("/dashboard/briefing")
      .then((r) => { if (alive) setBriefing(r.data); })
      .catch(() => { /* seamless: keep deterministic wording */ });
    return () => { alive = false; };
  }, [loaded, data]);

  const opportunities = data?.opportunities || [];
  const kpis = data?.kpis || {};
  const firstName = data?.summary?.first_name || user?.first_name || user?.name?.split(" ")[0] || "there";
  const summaryParts = data?.summary?.parts || [];
  const total = data?.summary?.total ?? opportunities.length;

  const kpiCards = useMemo(() => ([
    { icon: TrendingUp, label: "New Matches", value: kpis.new_matches ?? "—", tone: "gold",
      to: "/opportunities", testid: "metric-new-matches",
      sublabel: (kpis.new_matches > 0) ? `${kpis.new_matches} strong match${kpis.new_matches === 1 ? "" : "es"} waiting` : null,
      subTo: "/opportunities", subTestid: "new-matches-waiting-link" },
    { icon: Users, label: "Active Buyer Needs", value: kpis.active_buyer_needs ?? "—", to: "/my-buyers", testid: "metric-active-buyers" },
    { icon: Building2, label: "Active Properties", value: kpis.active_properties ?? "—", to: "/my-properties", testid: "metric-active-properties" },
    { icon: Inbox, label: "New Inquiries", value: kpis.new_inquiries ?? "—", to: "/inquiries", testid: "metric-new-inquiries" },
    { icon: Handshake, label: "Active Collaborations", value: kpis.active_collaborations ?? "—", to: "/collaborations", testid: "metric-active-collaborations" },
  ]), [kpis]);

  const kpiSpans = [
    "col-span-1 xl:col-span-2",
    "col-span-1 xl:col-span-2",
    "col-span-1 xl:col-span-2",
    "col-span-1 xl:col-span-3",
    "col-span-2 xl:col-span-3",
  ];

  return (
    <PageShell testid="command-center-page">
      <VerificationBanner />
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-accent">
          <Sparkles className="h-3.5 w-3.5" /> My Command Center
        </div>
        <Link to="/" data-testid="command-center-feed-link"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:gap-2.5 transition-all">
          <LayoutGrid className="h-4 w-4" /> Opportunity Feed <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
      <Fade>
        <section data-testid="command-center" className="rounded-2xl border border-line-subtle bg-card overflow-hidden shadow-[0_1px_2px_rgba(15,44,89,0.05),0_12px_32px_-18px_rgba(15,44,89,0.16)]">
          <div className="flex flex-col xl:flex-row">
            <div data-testid="welcome-header" className="xl:w-[57%] p-4 sm:px-6 sm:pt-4 sm:pb-3.5">
              <div className="min-w-0">
                <h1 className="font-display text-[22px] sm:text-[24px] xl:text-[26px] leading-tight font-bold tracking-tight text-slate-900">
                  {greetingFor()}, {firstName}
                </h1>
              </div>
              {verifiedPro && (
                <div data-testid="quick-actions" className="mt-3 flex flex-wrap gap-2.5">
                  <Link to="/have-property" data-testid="quick-action-add-property"
                    className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold text-sm px-4 py-2.5 rounded-xl hover:bg-primary/90 active:scale-95 transition-all">
                    <Home className="h-4 w-4" /> Add Property
                  </Link>
                  <Link to="/have-buyer" data-testid="quick-action-post-buyer"
                    className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-semibold text-sm px-4 py-2.5 rounded-xl hover:bg-accent/90 active:scale-95 transition-all">
                    <UserPlus className="h-4 w-4" /> Post Buyer Need
                  </Link>
                  <Link to="/post-event" data-testid="quick-action-post-event"
                    className="inline-flex items-center gap-2 border border-primary text-primary bg-transparent font-semibold text-sm px-4 py-2.5 rounded-xl hover:bg-primary/5 active:scale-95 transition-all">
                    <CalendarDays className="h-4 w-4" /> Post Event
                  </Link>
                </div>
              )}
              <div data-testid="ai-insight" className="mt-3 flex items-start gap-2.5 rounded-lg border border-ai-border border-l-[3px] border-l-gold-500 bg-ai-surface px-3 py-2">
                <Sparkles className="h-4 w-4 text-gold-500 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div data-testid="ai-insight-badge" className="text-[10px] font-bold uppercase tracking-[0.14em] text-gold-600 leading-none mb-1">PropertyConnect Insight</div>
                  <p data-testid="opportunity-summary" className="text-slate-700 text-sm sm:text-base leading-snug">
                    {!verifiedPro
                      ? "Complete Professional Verification to unlock matching, listings, buyer needs and your Sales Coach. You can finish verification from your Profile."
                      : !loaded
                      ? "Gathering today's opportunities…"
                      : briefing?.meta?.source === "ai" && briefing?.briefing
                        ? briefing.briefing
                        : total > 0
                          ? <>You have <span className="font-semibold text-slate-900">{total}</span> {total === 1 ? "opportunity" : "opportunities"} worth acting on today{summaryParts.length > 0 ? <>. <span className="text-slate-600">{summaryParts.join(" · ")}</span></> : "."}</>
                          : data?.summary?.is_new
                            ? "Start by adding a property or posting a buyer need so PropertyConnect can begin finding opportunities for you."
                            : "You're all caught up. We'll surface new opportunities here as they appear."}
                  </p>
                </div>
              </div>
            </div>

            <div data-testid="kpi-panel" className="xl:w-[43%] border-t xl:border-t-0 xl:border-l border-border">
              <div className="grid grid-cols-2 xl:grid-cols-6 gap-px bg-border h-full">
                {kpiCards.map((m, i) => (
                  <div key={m.label} className={`bg-card flex ${kpiSpans[i]}`}>
                    <MetricCell {...m} value={loaded ? m.value : "—"} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </Fade>

      {verifiedPro && (
        <Fade delay={0.05}>
          <Link to="/leads" data-testid="dashboard-leads-strip"
            className="mt-4 flex items-center justify-between gap-4 rounded-xl border border-border bg-card px-4 py-3 hover:shadow-md transition-shadow group">
            <div className="flex items-center gap-3 min-w-0">
              <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0"><Contact className="h-5 w-5 text-primary" /></div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                <span className="font-semibold text-slate-900">Leads</span>
                <span className="text-slate-600"><span className="font-bold text-slate-900">{loaded ? (kpis.active_leads ?? 0) : "—"}</span> active</span>
                <span className="text-slate-600 flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5 text-critical" /><span className="font-bold text-critical">{loaded ? (kpis.follow_ups_due ?? 0) : "—"}</span> follow-ups due</span>
                <span className="text-slate-600"><span className="font-bold text-accent">{loaded ? (kpis.qualified_leads ?? 0) : "—"}</span> qualified</span>
              </div>
            </div>
            <span className="text-sm font-semibold text-primary flex items-center gap-1 shrink-0">View pipeline <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" /></span>
          </Link>
        </Fade>
      )}

      <div className="mt-5 grid grid-cols-1 xl:grid-cols-5 gap-5 items-start" data-testid="coach-spotlight-row">
        {verifiedPro && (
          <div className="xl:col-span-2 min-w-0" data-testid="coach-column">
            <SalesCoachModule embedded />
          </div>
        )}
        <div className={`${verifiedPro ? "xl:col-span-3" : "xl:col-span-5"} min-w-0`} data-testid="spotlight-column">
          <PropConnectSpotlight embedded />
        </div>
      </div>
    </PageShell>
  );
}
